import React, { useState, useEffect } from 'react';
import { Table, Button, Select, message, DatePicker, Popconfirm } from 'antd';
import { ReloadOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import dayjs from 'dayjs';
import '../../styles/third-party.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ThirdPartySettlements = () => {
  const [loading, setLoading] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetchApps();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSettlements();
  }, [page, filters]);

  const fetchApps = async () => {
    try {
      const response = await api.get('/admin/third-party/apps', { params: { limit: 100 } });
      setApps(response.data.list || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/third-party/settlements/statistics');
      setStats(response.data || {});
    } catch (error) {
      console.error(error);
    }
  };

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const params = { page, limit, ...filters };
      if (filters.dateRange) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        delete params.dateRange;
      }
      const response = await api.get('/admin/third-party/settlements', { params });
      setSettlements(response.data.list || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      message.error('获取结算列表失败');
    }
    setLoading(false);
  };

  const handleSettle = async (id) => {
    try {
      await api.post(`/admin/third-party/settlements/${id}/settle`);
      message.success('结算成功');
      fetchSettlements();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '结算失败');
    }
  };

  const handleBatchSettle = async () => {
    const selectedRows = settlements.filter(s => s.status === 'pending');
    if (selectedRows.length === 0) {
      message.warning('没有待结算的记录');
      return;
    }
    
    try {
      const ids = selectedRows.map(s => s.id);
      await api.post('/admin/third-party/settlements/batch-settle', { ids });
      message.success('批量结算成功');
      fetchSettlements();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '结算失败');
    }
  };

  const handleReset = () => {
    setFilters({});
    setPage(1);
  };

  const CURRENCY_SYMBOLS = { CNY: '¥', USD: '$', EUR: '€', GBP: '£', KES: 'KSh' };

  const formatMoney = (val, currencyCode = 'CNY') => {
    const code = currencyCode || 'CNY';
    const symbol = CURRENCY_SYMBOLS[code] || '';
    const num = Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return { symbol, num, code };
  };

  const renderAmount = (val, currencyCode) => {
    const { symbol, num, code } = formatMoney(val, currencyCode);
    return (
      <div className="tp-amount">
        <span className="tp-amount__value">{symbol}{num}</span>
        <span className="tp-amount__ccy">{code}</span>
      </div>
    );
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { tone: 'processing', text: '待结算' },
      success: { tone: 'paid', text: '已结算' },
      failed: { tone: 'failed', text: '结算失败' }
    };
    const config = statusMap[status] || { tone: 'muted', text: status };
    return <span className={`tp-tag tp-tag--${config.tone}`}>{config.text}</span>;
  };

  const columns = [
    {
      title: '结算ID',
      dataIndex: 'id',
      key: 'id',
      width: 88,
      render: (val) => val ? <span className="tp-mono">{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '订单号',
      dataIndex: 'trade_no',
      key: 'trade_no',
      width: 196,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '应用名称',
      dataIndex: 'app_name',
      key: 'app_name',
      width: 140,
      render: (val) => val ? <span className="tp-mono" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '货币',
      dataIndex: 'currency_code',
      key: 'currency_code',
      width: 88,
      render: (val) => <span className="tp-code">{val || 'CNY'}</span>
    },
    {
      title: '订单金额',
      dataIndex: 'order_amount',
      key: 'order_amount',
      width: 140,
      render: (val, record) => renderAmount(val, record.currency_code)
    },
    {
      title: '手续费率',
      dataIndex: 'fee_rate',
      key: 'fee_rate',
      width: 100,
      render: (val) => <span className="tp-mono">{(parseFloat(val) * 100).toFixed(2)}%</span>
    },
    {
      title: '手续费',
      dataIndex: 'fee_amount',
      key: 'fee_amount',
      width: 140,
      render: (val, record) => renderAmount(val, record.currency_code)
    },
    {
      title: '结算金额',
      dataIndex: 'settle_amount',
      key: 'settle_amount',
      width: 140,
      render: (val, record) => renderAmount(val, record.currency_code)
    },
    // {
    //   title: '汇率',
    //   dataIndex: 'exchange_rate',
    //   key: 'exchange_rate',
    //   render: (val) => val ? parseFloat(val).toFixed(4) : '-',
    //   width: 80
    // },
    // {
    //   title: '人民币金额',
    //   dataIndex: 'settle_amount_cny',
    //   key: 'settle_amount_cny',
    //   render: (val) => val ? `¥${parseFloat(val).toFixed(2)}` : '-'
    // },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: '结算时间',
      dataIndex: 'settle_time',
      key: 'settle_time',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => time ? <span className="tp-time">{dayjs(time).format('YYYY-MM-DD HH:mm')}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 88,
      fixed: 'right',
      render: (_, record) => (
        record.status === 'pending' ? (
          <div className="tp-actions">
            <Popconfirm title="确认结算?" onConfirm={() => handleSettle(record.id)}>
              <Button type="link" size="small" icon={<CheckOutlined />}>
                结算
              </Button>
            </Popconfirm>
          </div>
        ) : <span className="tp-empty">-</span>
      )
    }
  ];

  return (
    <div className="tp-page">
      <div className="tp-card tp-summary">
        <div className="tp-summary__item">
          <div className="tp-summary__label">结算总笔数</div>
          <div className="tp-summary__value">{Number(stats.total_count || 0).toLocaleString('en-US')}</div>
        </div>
        <div className="tp-summary__item">
          <div className="tp-summary__label">订单总金额</div>
          <div className="tp-summary__value">{Number(stats.total_order_amount_cny || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="tp-summary__item">
          <div className="tp-summary__label">结算总金额</div>
          <div className="tp-summary__value tp-summary__value--accent">{Number(stats.total_settle_amount_cny || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>
        <div className="tp-summary__item">
          <div className="tp-summary__label">待结算笔数</div>
          <div className="tp-summary__value tp-summary__value--green">{Number(stats.pending_count || 0).toLocaleString('en-US')}</div>
        </div>
      </div>

      <div className="tp-card tp-toolbar">
        <div className="tp-filters">
          <Select
            placeholder="选择应用"
            allowClear
            className="tp-field--md"
            onChange={(val) => setFilters({ ...filters, app_id: val })}
            value={filters.app_id}
          >
            {apps.map(app => (
              <Option key={app.app_id} value={app.app_id}>{app.app_name}</Option>
            ))}
          </Select>
          <Select
            placeholder="结算状态"
            allowClear
            className="tp-field--sm"
            onChange={(val) => setFilters({ ...filters, status: val })}
            value={filters.status}
          >
            <Option value="pending">待结算</Option>
            <Option value="success">已结算</Option>
            <Option value="failed">失败</Option>
          </Select>
          <RangePicker
            className="tp-field--date"
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            value={filters.dateRange}
          />
          <div className="tp-filters__actions">
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleBatchSettle}>
              批量结算
            </Button>
          </div>
        </div>
      </div>

      <div className="tp-card tp-table-card">
        <Table
          columns={columns}
          dataSource={settlements}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 1480 }}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`
          }}
        />
      </div>
    </div>
  );
};

export default ThirdPartySettlements;
